import { useEffect, useState } from "react";
import { urlifyName } from "../../shared/Utils/PageUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as faSolidStar } from "@fortawesome/free-solid-svg-icons";
import { faStar as faRegularStar } from "@fortawesome/free-regular-svg-icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getPaginatedNonprofits } from "../../../api/Nonprofits";
import { addFavorite, removeFavorite } from "../../../api/Favorite";
import { useSelector } from "react-redux";
import Spinner from "../../shared/LoadingSpinner/Spinner";
import { states } from "states-us";
import SearchHeader from "./SearchHeader";
import { MapPinLine } from "@phosphor-icons/react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { extractTextContent } from "../../shared/Utils/ExtractText";
import PaginationButtons from "../../shared/Buttons/pagination";

export default function NonprofitSearch() {
  const [nonprofits, setNonprofits] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchParam, setSearchParam] = useState(
    searchParams.get("search") || ""
  );
  const [localUserData, setLocalUserData] = useState(null);
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userData.userData);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [stateParam, setStateParam] = useState("");

  // getting list of nonprofits from api
  const getAllNonprofits = async (page, searchParam, stateParam) => {
    try {
      setIsLoading(true);
      const response = await getPaginatedNonprofits(
        page,
        searchParam,
        stateParam
      );

      if (response) {
        setIsLoading(false);
        // Ensure nonprofits is always an array, even if response.results is undefined
        setNonprofits(response.results || []);

        // Calculate total pages (assuming 12 nonprofits per page)
        const totalCount = response.count || 0;
        const calculatedTotalPages = Math.ceil(totalCount / 12);
        setTotalPages(calculatedTotalPages);
      } else {
        // Handle case where response is null/undefined
        setIsLoading(false);
        setNonprofits([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error(`Could not get nonprofits`, error);
      setIsLoading(false);
      setNonprofits([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    getAllNonprofits(page, "", "");
  }, []);

  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      setLocalUserData(JSON.parse(userData));
    }
  }, [userData]);

  // Scrolls to the top of the page only on first render
  useEffect(() => {
    if (page === 1) {
      window.scrollTo(0, 0);
    }
  }, [page]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchParam(e.target.value);
  };

  // Handle state dropdown change
  const handleStateChange = (e) => {
    const newStateParam = e.target.value;
    setStateParam(newStateParam);
    setPage(1);
    getAllNonprofits(1, searchParam, newStateParam);
  };

  // Handle search execution (when user presses Enter or clicks search)
  const handleSearch = () => {
    setPage(1);
    getAllNonprofits(1, searchParam, stateParam);
  };

  // Handle key down events (search on Enter key)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
    getAllNonprofits(newPage, searchParam, stateParam);
  };

  // Search when search bar becomes empty
  useEffect(() => {
    if (searchParam === "") {
      handleSearch();
    }
  }, [searchParam]);

  const handleAddFavorite = async (id) => {
    if (!localUserData) return;
    try {
      await addFavorite({ nonprofit_id: id });
      const i = localUserData.favorite_nonprofits.indexOf(id);
      if (i < 0) {
        const newFavorites = [...localUserData.favorite_nonprofits, id];
        setLocalUserData({
          ...localUserData,
          favorite_nonprofits: newFavorites,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveFavorite = async (id) => {
    if (!localUserData) return;
    try {
      await removeFavorite({ nonprofit_id: id });
      const i = localUserData.favorite_nonprofits.indexOf(id);
      if (i >= 0) {
        const newFavorites = localUserData.favorite_nonprofits.filter(
          (favId) => favId !== id
        );
        setLocalUserData({
          ...localUserData,
          favorite_nonprofits: newFavorites,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleStarClick = (event, isFavorite, id) => {
    event.stopPropagation();
    event.preventDefault();
    if (isFavorite) {
      handleRemoveFavorite(id);
    } else {
      handleAddFavorite(id);
    }
  };

  return (
    <div className="mb-8 sm:grow">
      <SearchHeader
        searchValue={searchParam}
        stateValue={stateParam}
        handleSearchChange={handleSearchChange}
        handleStateChange={handleStateChange}
        handleKeyDown={handleKeyDown}
        states={states}
      />

      <div className="flex flex-col my-8">
        {isLoading ? (
          <div className="flex flex-row mx-auto mt-16 w-full pb-24 justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto px-4">
              {nonprofits && nonprofits.length > 0 ? (
                nonprofits.map((item, index) => (
                  <Link
                    to={`/non-profit/${urlifyName(item.org_name)}/${item.id}`}
                    key={index}
                    className="block"
                  >
                    <div
                      className="relative bg-white rounded-2xl overflow-hidden max-w-[278px] w-full mx-auto cursor-pointer select-none flex flex-col"
                      style={{
                        height: "380px",
                        boxShadow: "0px 4px 20px -4px rgba(0, 0, 0, 0.25)",
                      }}
                    >
                      {/* Logo Section */}
                      <div className="p-2">
                        <div className="w-[263px] h-[207px] rounded-2xl overflow-hidden mx-auto flex items-center justify-center bg-gray-50">
                          {item.logo ? (
                            <LazyLoadImage
                              src={item.logo}
                              alt={`Logo of ${item.org_name}`}
                              className="max-w-full max-h-full object-contain select-none pointer-events-none"
                              effect="opacity"
                              wrapperClassName="w-full h-full flex items-center justify-center"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-sm">
                                No Logo
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="p-2 text-left flex-1 flex flex-col">
                        {/* Star Favorite Button */}
                        {localUserData && (
                          <div className="absolute top-4 right-4 z-10">
                            {localUserData.favorite_nonprofits.includes(
                              item.id
                            ) ? (
                              <FontAwesomeIcon
                                icon={faSolidStar}
                                onClick={(e) =>
                                  handleStarClick(e, true, item.id)
                                }
                                className="cursor-pointer text-orange w-5 h-5 hover:text-orange-dark1"
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faRegularStar}
                                onClick={(e) =>
                                  handleStarClick(e, false, item.id)
                                }
                                className="cursor-pointer text-orange w-5 h-5 hover:text-orange-dark1"
                              />
                            )}
                          </div>
                        )}

                        {/* Organization Name */}
                        <h2
                          className="text-orange !font-montserrat text-lg font-bold mb-1"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxHeight: "3rem",
                            lineHeight: "1.5rem",
                          }}
                        >
                          {item.org_name}
                        </h2>

                        {/* Location */}
                        <div className="flex items-center mb-3">
                          <MapPinLine
                            size={20}
                            className="text-grey-featuredCampaign mr-2"
                          />
                          <span className="text-grey-featuredCampaign text-xs !font-montserrat">
                            {item.address
                              ? `${item.address.city}, ${item.address.state}`
                              : "Location not specified"}
                          </span>
                        </div>

                        {/* Mission Statement */}
                        <p
                          className="mb-4 !text-xs !font-montserrat"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minHeight: "3.6rem",
                            maxHeight: "3.6rem",
                            lineHeight: "1.2rem",
                          }}
                        >
                          {item.mission_stmt
                            ? (() => {
                                const cleanText = extractTextContent(
                                  item.mission_stmt
                                );
                                return cleanText.length > 120
                                  ? cleanText.substring(0, 120) + "..."
                                  : cleanText;
                              })()
                            : "Mission statement not available"}
                        </p>

                        <div className="flex-grow"></div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center py-8">
                  <p>No nonprofits found!</p>
                </div>
              )}
            </div>

            {!isLoading && nonprofits.length > 0 && totalPages > 1 && (
              <PaginationButtons
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                className="mt-16"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
